// contexts/HeaderContext.tsx
import { FluentIcon } from '@fluentui/react-icons'
import { createContext, useContext, ReactNode, useState, useCallback, useMemo, useEffect } from 'react'

interface HeaderRightTitle {
  text: string
  icon: FluentIcon
}

interface HeaderStateContent {
  title1: string
  title2?: string
  titleUrl?: string
  privacyUrl?: string
  rightTitle?: HeaderRightTitle
}

interface HeaderContextType extends HeaderStateContent {
  setHeaderContent: (params: Partial<HeaderStateContent>) => void
}

const HeaderContext = createContext<HeaderContextType | undefined>(undefined)

const DEFAULT_HEADER_STATE: HeaderStateContent = {
  title1: 'Data Modelling Platform',
  title2: undefined,
  titleUrl: undefined,
  privacyUrl: undefined,
  rightTitle: undefined,
}

export function HeaderProvider({ children }: { children: ReactNode }) {
  const [headerContent, setHeaderContent] = useState<HeaderStateContent>(DEFAULT_HEADER_STATE)

  const setContent = useCallback(
    (params: {
      title1?: string
      title2?: string
      titleUrl?: string
      privacyUrl?: string
      rightTitle?: HeaderRightTitle
    }) =>
      setHeaderContent((current) => ({
        ...current,
        ...params,
      })),
    []
  )

  const value = useMemo(
    () => ({
      ...headerContent,
      setHeaderContent: setContent,
    }),
    [headerContent, setContent]
  )

  return <HeaderContext.Provider value={value}>{children}</HeaderContext.Provider>
}

export function useHeader() {
  const context = useContext(HeaderContext)
  if (!context) {
    throw new Error('useHeader must be used within a HeaderProvider')
  }
  return context
}

export function useSetHeaderContext(params: {
  title1?: string
  title2?: string
  titleUrl?: string
  privacyUrl?: string
  rightTitle?: HeaderRightTitle
}) {
  const { setHeaderContent } = useHeader()

  const { title1, title2, titleUrl, privacyUrl, rightTitle: { text, icon } = {} } = params
  useEffect(() => {
    setHeaderContent({ title1, title2, titleUrl, privacyUrl, rightTitle: text && icon ? { text, icon } : undefined })

    // Clean up when component unmounts
    return () => setHeaderContent(DEFAULT_HEADER_STATE)
  }, [icon, privacyUrl, setHeaderContent, text, title1, title2, titleUrl])
}
